import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowLeft01Icon,
  InformationCircleIcon,
  Search01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import {
  PRERECORDED_VOICEMAILS_QUERY_KEY,
  type PrerecordedVoicemail
} from "api/prerecordedVoicemails";
import { usePickableVoicemails } from "./usePickableVoicemails";
import { VoicemailGlyph } from "shared/components/VoicemailGlyph";
import { formatVoicemailClock } from "shared/utils/voicemail-drop";
import { Avatar } from "shared/components/Avatar";
import { cx } from "shared/utils";
import {
  VoicemailDropError,
  firstName,
  useSoftphone,
  type ActiveVoicemailDrop
} from "./SoftphoneProvider";

// The search box shows once the list is longer than this.
const SEARCH_FROM = 6;
const REMOVED_CODE = "voicemail_removed";

/**
 * The Leave a voicemail view of the call card (CRM-1830): the company's
 * pre-recorded messages with their length. Picking one plays it into the call
 * at once and ends the teammate's side of the call.
 */
export function VoicemailPanel({
  callerName,
  elapsedLabel,
  onClose
}: {
  callerName: string;
  elapsedLabel: string;
  // Back to the call.
  onClose: () => void;
}) {
  const { leaveVoicemail } = useSoftphone();
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = usePickableVoicemails(true);
  const [query, setQuery] = useState("");
  const [leavingId, setLeavingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const voicemails = useMemo(() => data ?? [], [data]);
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return needle
      ? voicemails.filter((voicemail) =>
          voicemail.name.toLowerCase().includes(needle)
        )
      : voicemails;
  }, [voicemails, query]);

  const pick = async (voicemail: PrerecordedVoicemail) => {
    setLeavingId(voicemail.id);
    setError(null);
    try {
      await leaveVoicemail(voicemail);
    } catch (err) {
      setLeavingId(null);
      setError(err instanceof Error ? err.message : String(err));
      // An admin removed it while the list was open: show what's left.
      if (err instanceof VoicemailDropError && err.code === REMOVED_CODE)
        queryClient.invalidateQueries({
          queryKey: PRERECORDED_VOICEMAILS_QUERY_KEY
        });
    }
  };

  return (
    <div className="w-full text-left">
      <div className="flex items-start gap-2 pr-8">
        <button
          type="button"
          aria-label="Back"
          onClick={onClose}
          disabled={leavingId !== null}
          className="grid size-8 shrink-0 place-items-center rounded-full text-content-400 transition hover:bg-alpha-40 hover:text-content-1000"
        >
          <HugeiconsIcon icon={ArrowLeft01Icon} size={20} />
        </button>
        <div className="min-w-0 pt-1">
          <p className="text-base font-semibold text-content-1000">
            Leave a voicemail
          </p>
          <p className="truncate text-xs text-content-400">
            {callerName} · {elapsedLabel}
          </p>
        </div>
      </div>

      <p className="mt-4 flex items-center gap-2 rounded-lg bg-primary-20 px-3 py-2 text-sm font-medium text-primary-600">
        <HugeiconsIcon icon={InformationCircleIcon} size={16} />
        <span>Wait for the beep, then pick a message.</span>
      </p>

      {voicemails.length >= SEARCH_FROM ? (
        <label className="mt-3 flex items-center gap-2 rounded-lg px-3 py-2 ring-1 ring-alpha-100 focus-within:ring-2 focus-within:ring-primary-500">
          <HugeiconsIcon
            icon={Search01Icon}
            size={18}
            className="text-content-300"
          />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search messages"
            aria-label="Search messages"
            className="w-full border-0 bg-transparent p-0 text-sm text-content-1000 placeholder:text-content-300 focus:outline-none focus:ring-0"
          />
        </label>
      ) : null}

      {error ? <p className="mt-3 text-xs text-danger-500">{error}</p> : null}

      <div className="mt-3 max-h-[320px] overflow-y-auto">
        <p className="mb-1 px-1 text-xs font-medium uppercase tracking-wide text-content-300">
          Company messages
        </p>
        <ListBody
          loading={isLoading}
          failed={isError}
          voicemails={shown}
          query={query}
          leavingId={leavingId}
          onPick={(voicemail) => void pick(voicemail)}
        />
      </div>
    </div>
  );
}

function ListBody({
  loading,
  failed,
  voicemails,
  query,
  leavingId,
  onPick
}: {
  loading: boolean;
  failed: boolean;
  voicemails: PrerecordedVoicemail[];
  query: string;
  leavingId: number | null;
  onPick: (voicemail: PrerecordedVoicemail) => void;
}) {
  if (loading)
    return (
      <p className="py-6 text-center text-sm text-content-400">Loading…</p>
    );
  if (failed)
    return (
      <p className="py-6 text-center text-sm text-danger-500">
        Couldn&apos;t load the messages. Go back and try again.
      </p>
    );
  if (voicemails.length === 0)
    return (
      <p className="px-1 py-3 text-sm text-content-400">
        {query.trim()
          ? `No messages match “${query.trim()}”.`
          : "No pre-recorded voicemails yet."}
      </p>
    );

  return (
    <>
      {voicemails.map((voicemail) => (
        <button
          key={voicemail.id}
          type="button"
          disabled={leavingId !== null}
          onClick={() => onPick(voicemail)}
          className={cx(
            "flex w-full items-center gap-3 rounded-lg px-1 py-2 text-left transition hover:bg-alpha-40 disabled:cursor-not-allowed",
            leavingId === voicemail.id ? "bg-alpha-40" : ""
          )}
        >
          <span className="grid size-9 shrink-0 place-items-center rounded-full bg-primary-20 text-primary-500">
            <VoicemailGlyph />
          </span>
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-content-1000">
            {voicemail.name}
          </span>
          <span className="shrink-0 text-xs tabular-nums text-content-400">
            {leavingId === voicemail.id
              ? "Leaving…"
              : formatVoicemailClock(voicemail.duration_seconds)}
          </span>
        </button>
      ))}
    </>
  );
}

/**
 * The card right after a message was picked (CRM-1830): the teammate is off
 * the call and can place the next one; it closes on its own.
 */
export function VoicemailLeavingStatus({
  drop,
  callerName,
  displayName
}: {
  drop: ActiveVoicemailDrop;
  callerName: string;
  displayName: string | undefined;
}) {
  return (
    <div className="flex flex-col items-center py-2 text-center">
      <Avatar name={displayName} size={72} className="text-2xl font-semibold" />
      <p className="mt-4 text-base font-semibold text-content-1000">
        {callerName}
      </p>
      <div className="mt-6 w-full rounded-xl bg-primary-20 px-4 py-3">
        <p className="flex items-center justify-center gap-1.5 text-base font-semibold text-primary-600">
          <VoicemailGlyph />
          <span className="truncate">
            Leaving “{drop.name}” ({formatVoicemailClock(drop.durationSeconds)})
          </span>
        </p>
        <p className="mt-0.5 text-xs text-content-400">
          You&apos;re off the call. Boom plays the message into{" "}
          {displayName ? `${firstName(displayName)}'s` : "their"} voicemail,
          then hangs up.
        </p>
      </div>
      <p className="mt-4 text-xs text-content-300">
        You can place your next call now.
      </p>
    </div>
  );
}
